"use client";

import React, { useEffect, useState } from "react";
import { CircleCheck, ArrowRight, Zap, Lock, Smartphone, Clock, MessageCircle } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * OurDesignPhilosophy — "Eine Website ist kein Luxus mehr"
 * Variante A: Industries Carousel + Floating Proof Badges
 *
 * Linke Spalte: bestehender Text + 4 Bullet-Points + CTA (unverändert).
 * Rechte Spalte: Desktop + Phone Mockup, Screen-Inhalt rotiert alle 4.5s
 * durch 4 Branchen mit Crossfade. Drumherum schweben 5 Proof-Badges, die
 * jeweils einen der Bullet-Points visuell beweisen (PageSpeed, SSL,
 * Mobile-perfekt, Online-Zeit, CH Support). Mobile: Badges werden zu
 * einem kompakten Pill-Strip darunter.
 */

type Industry = {
  label: string;
  url: string;
  image: string;
};
const INDUSTRIES: Industry[] = [{
  label: "Restaurant",
  url: "zurichresto.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/restaurant.png-scaled.jpg?v=j1jzzezydc"
}, {
  label: "Coiffeur",
  url: "marias-coiffeur.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Hair-Salo-scaled.png?v=co1azvw1u8q"
}, {
  label: "Immobilien",
  url: "vista-properties.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Real-Estate-scaled.png?v=pyzxuisv23"
}, {
  label: "Handwerk",
  url: "colorcraft-bern.ch",
  image: "https://pagemaker.my-pagemaker24.com/wp-content/uploads/2026/04/Moler-scaled.png?v=a6tiktpldx"
}];
const points = ["Kein Technik-Wissen nötig – jeder kann es, auch ohne Vorkenntnisse", "Auf Google gefunden werden – SEO von Anfang an eingebaut", "Mehr Kunden gewinnen – eine professionelle Website schafft Vertrauen", "Persönlicher Support auf Deutsch – per Chat, E-Mail oder Video"];
export default function OurDesignPhilosophy() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const [idx, setIdx] = useState(0);
  const [urlVisible, setUrlVisible] = useState(true);

  // Preload images
  useEffect(() => {
    INDUSTRIES.forEach(ind => {
      const img = new Image();
      img.src = ind.image;
    });
  }, []);

  // Auto-rotate every 4.5s with URL fade-swap
  useEffect(() => {
    const interval = setInterval(() => {
      setUrlVisible(false);
      setTimeout(() => {
        setIdx(prev => (prev + 1) % INDUSTRIES.length);
        setUrlVisible(true);
      }, 300);
    }, 4500);
    return () => clearInterval(interval);
  }, []);
  return <section data-section-id="109679" id="direkte-einstiege" className="bg-background text-foreground py-[120px] md:py-[160px] overflow-hidden relative">
      <style>{`
        @keyframes odp-pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: .4; } }
        .odp-pulse-dot { animation: odp-pulse-dot 1.6s ease-in-out infinite; }

        @keyframes odp-bob1 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes odp-bob2 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes odp-bob3 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes odp-bob4 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
        @keyframes odp-bob5 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }

        @keyframes odp-badge-in { to { opacity: 1; } }

        .odp-badge-1 { opacity: 0; animation: odp-badge-in 0.7s ease-out 0.6s forwards, odp-bob1 4.2s ease-in-out 1.3s infinite; }
        .odp-badge-2 { opacity: 0; animation: odp-badge-in 0.7s ease-out 0.8s forwards, odp-bob2 5.0s ease-in-out 1.5s infinite; }
        .odp-badge-3 { opacity: 0; animation: odp-badge-in 0.7s ease-out 1.0s forwards, odp-bob3 4.6s ease-in-out 1.7s infinite; }
        .odp-badge-4 { opacity: 0; animation: odp-badge-in 0.7s ease-out 1.2s forwards, odp-bob4 5.2s ease-in-out 1.9s infinite; }
        .odp-badge-5 { opacity: 0; animation: odp-badge-in 0.7s ease-out 1.4s forwards, odp-bob5 4.8s ease-in-out 2.1s infinite; }

        @keyframes odp-device-in {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .odp-device-in { opacity: 0; animation: odp-device-in 0.9s cubic-bezier(0.22,1,0.36,1) 0.1s forwards; }
        .odp-device-in-delayed { opacity: 0; animation: odp-device-in 0.9s cubic-bezier(0.22,1,0.36,1) 0.4s forwards; }

        .odp-url-fade { transition: opacity 0.3s ease; }
      `}</style>

      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* ════════ LEFT (unverändert) ════════ */}
          <div ref={leftRef} className={`flex flex-col gap-10 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium tracking-wide w-fit">
                <span className="w-2 h-2 rounded-full bg-primary odp-pulse-dot" />
                Für Schweizer KMU &amp; Gründer
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1]">
                Eine Website ist kein Luxus mehr
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                Einfach. Schnell. Günstig. Erstellen Sie Ihre professionelle
                Website in unter einer Stunde – und werden Sie dort gefunden, wo
                Ihre Kunden suchen: auf Google.
              </p>
            </div>

            <ul className="space-y-5">
              {points.map((point, i) => <li key={i} className="flex items-start gap-4 group">
                  <div className="mt-1 bg-primary/10 p-1 rounded-full group-hover:bg-primary/20 transition-colors">
                    <CircleCheck className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-lg text-foreground/90 font-medium">
                    {point}
                  </span>
                </li>)}
            </ul>

            <div className="pt-4">
              <a href="https://dashboard.pagemaker24.ch/sign-up" className="group inline-flex items-center justify-center gap-2 h-14 px-8 text-base font-medium rounded-full text-white bg-primary shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300">
                Jetzt kostenlos starten
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1.5" />
              </a>
            </div>
          </div>

          {/* ════════ RIGHT — Carousel + Badges ════════ */}
          <div ref={rightRef} className={`relative pt-8 pb-12 lg:pt-16 lg:pb-20 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            {/* Halo background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent rounded-[3rem] blur-2xl pointer-events-none" />

            {/* Devices group */}
            <div className="relative mx-auto" style={{
            maxWidth: "560px"
          }}>
              {/* Main desktop frame */}
              <div className="odp-device-in relative bg-foreground rounded-t-2xl rounded-b-md p-2.5 shadow-2xl">
                {/* Browser chrome */}
                <div className="bg-secondary rounded-t-md px-3 py-2 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-300" />
                  </div>
                  <div className="flex-1 mx-3 bg-background rounded px-3 py-0.5 flex items-center gap-1.5 max-w-[260px]">
                    <Lock className="w-3 h-3 text-muted-foreground shrink-0" strokeWidth={2.5} />
                    <span className="odp-url-fade text-[11px] text-muted-foreground font-medium truncate" style={{
                    opacity: urlVisible ? 1 : 0
                  }}>
                      {INDUSTRIES[idx].url}
                    </span>
                  </div>
                </div>
                {/* Screen with crossfade stack */}
                <div className="relative aspect-[16/10] bg-background overflow-hidden rounded-b">
                  {INDUSTRIES.map((ind, i) => <img key={i} src={ind.image} alt={ind.label} className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700" style={{
                  opacity: i === idx ? 1 : 0
                }} />)}
                </div>
              </div>
              {/* Stand */}
              <div className="mx-auto h-2 w-1/3 bg-muted rounded-b-lg" />

              {/* Phone overlay (bottom-right) */}
              <div className="odp-device-in-delayed absolute -bottom-2 -right-4 md:-right-8 w-24 md:w-28 z-20">
                <div className="bg-foreground rounded-[1.5rem] p-1.5 shadow-2xl">
                  <div className="bg-background rounded-[1.2rem] overflow-hidden aspect-[9/16] relative">
                    {INDUSTRIES.map((ind, i) => <img key={i} src={ind.image} alt="" className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700" style={{
                    opacity: i === idx ? 1 : 0
                  }} />)}
                  </div>
                </div>
              </div>

              {/* ── Floating Badges (lg+ only) ── */}

              {/* Top-left: PageSpeed */}
              <div className="odp-badge-1 hidden lg:flex absolute -top-4 -left-12 items-center gap-2 bg-background border border-border rounded-full px-3 py-1.5 shadow-xl">
                <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-white shrink-0">
                  <Zap className="w-3.5 h-3.5" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground leading-none">PageSpeed</div>
                  <div className="text-sm font-bold text-foreground leading-tight">98/100</div>
                </div>
              </div>

              {/* Top-right: SSL aktiv */}
              <div className="odp-badge-2 hidden lg:flex absolute -top-2 -right-14 items-center gap-2 bg-background border border-border rounded-full px-3 py-1.5 shadow-xl">
                <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-green-600 shrink-0">
                  <Lock className="w-3.5 h-3.5" strokeWidth={2.5} />
                </div>
                <div className="text-sm font-semibold text-foreground pr-1">SSL aktiv</div>
              </div>

              {/* Middle-right: Mobile-perfekt */}
              <div className="odp-badge-3 hidden lg:flex absolute top-1/2 -right-20 -translate-y-1/2 items-center gap-2 bg-background border border-border rounded-full px-3 py-1.5 shadow-xl">
                <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-blue-600 shrink-0">
                  <Smartphone className="w-3.5 h-3.5" strokeWidth={2} />
                </div>
                <div className="text-sm font-semibold text-foreground pr-1">Mobile-perfekt</div>
              </div>

              {/* Bottom-left: Online in <60 Min */}
              <div className="odp-badge-4 hidden lg:flex absolute -bottom-4 -left-8 items-center gap-2 bg-background border border-border rounded-full px-3 py-1.5 shadow-xl">
                <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
                  <Clock className="w-3.5 h-3.5" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground leading-none">Online in</div>
                  <div className="text-sm font-bold text-foreground leading-tight">&lt; 60 Min</div>
                </div>
              </div>

              {/* Bottom-right: Schweizer Support */}
              <div className="odp-badge-5 hidden lg:flex absolute -bottom-12 right-12 items-center gap-2 bg-background border border-border rounded-full px-3 py-1.5 shadow-xl z-10">
                <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0">
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
                </div>
                <div className="text-sm font-semibold text-foreground pr-1">CH Support </div>
              </div>
            </div>

            {/* Industries indicator */}
            <div className="mt-12 lg:mt-16 flex items-center justify-center gap-1.5 flex-wrap">
              {INDUSTRIES.map((ind, i) => <React.Fragment key={ind.label}>
                  <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all duration-300 ${i === idx ? "text-white bg-primary" : "text-muted-foreground bg-transparent"}`}>
                    {ind.label}
                  </span>
                  {i < INDUSTRIES.length - 1 && <span className="text-muted-foreground/40">·</span>}
                </React.Fragment>)}
            </div>

            {/* Mobile-only compact badge strip */}
            <div className="lg:hidden mt-6 flex items-center justify-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 bg-background border border-border rounded-full px-3 py-1.5 shadow-sm text-xs font-semibold text-foreground">
                <Zap className="w-3 h-3 text-primary" strokeWidth={2.5} />
                98/100 PageSpeed
              </span>
              <span className="inline-flex items-center gap-1.5 bg-background border border-border rounded-full px-3 py-1.5 shadow-sm text-xs font-semibold text-foreground">
                <Lock className="w-3 h-3 text-green-600" strokeWidth={2.5} />
                SSL aktiv
              </span>
              <span className="inline-flex items-center gap-1.5 bg-background border border-border rounded-full px-3 py-1.5 shadow-sm text-xs font-semibold text-foreground">
                <Smartphone className="w-3 h-3 text-blue-600" strokeWidth={2} />
                Mobile-perfekt
              </span>
              <span className="inline-flex items-center gap-1.5 bg-background border border-border rounded-full px-3 py-1.5 shadow-sm text-xs font-semibold text-foreground">
                <Clock className="w-3 h-3 text-primary" strokeWidth={2} />
                &lt; 60 Min
              </span>
              <span className="inline-flex items-center gap-1.5 bg-background border border-border rounded-full px-3 py-1.5 shadow-sm text-xs font-semibold text-foreground">
                <MessageCircle className="w-3 h-3 text-red-600" strokeWidth={2} />
                CH Support
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}